import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Button, Checkbox, FormControlLabel, Typography, Collapse,
    Snackbar, Alert, TextField, Chip, Tooltip
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useRights } from "../context/RightsContext";
import { useConfigure } from "../context/ConfigureContext";

// Matches the rest of the dashboard's modern theme (Home2.js / DashboardItems.js /
// UserDefineColumns.jsx): #1877F2 blue accent, #EEF1F6 hairline borders, soft
// rgba(15,23,42,...) shadows, 14-18px radii, #0F172A / #64748B / #94A3B8 text tones.
const C = {
    primary:    '#1877F2',
    primaryDark:'#0855A8',
    primaryBg:  '#EBF3FF',
    success:    '#16A34A',
    successBg:  '#F0FDF4',
    warning:    '#D97706',
    warningBg:  '#FFFBEB',
    error:      '#DC2626',
    errorBg:    '#FEF2F2',
    border:     '#EEF1F6',
    surface:    '#F7FAFF',
    white:      '#FFFFFF',
    text:       '#0F172A',
    textSub:    '#64748B',
    textMuted:  '#94A3B8',
    locked:     '#F3F4F6',
    lockedText: '#94A3B8',
    lockedBorder:'#EEF1F6',
};

const GROUP_META = {
    Sales:    { icon: '📈', color: '#1877F2' },
    Purchase: { icon: '🛒', color: '#7C3AED' },
    Others:   { icon: '📝', color: '#059669' },
};

// Types excluded from flat cards — these are series-based with per-series config.
const SERIES_DOC_TYPES = new Set([
    // ❌ COMMENTED: Proforma Invoice
    // 'Proforma Invoice',
    'Sale Challan - Sale Invoice',
    'Sale Challan',
    'Sale Invoice',
    'Credit Note',
]);

const ConfigureModule = ({ connectionString }) => {
    const { user, selectedCompany, configureData, refreshConfigure } = useAuth();
    const { refetchConfigureData } = useConfigure();
    const navigate = useNavigate();
    const { canAccess, showAccessDenied, rightsReady, rightsLoading } = useRights();

    const userId    = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const appRegId  = selectedCompany?.AppRegID || null;

    useEffect(() => {
        if (!rightsReady || rightsLoading) return;

        if (canAccess && !canAccess('Config', 'view')) {
            showAccessDenied?.();
            navigate('/main/home', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rightsReady, rightsLoading]);

    const [form, setForm]               = useState([]);
    const [isDirty, setIsDirty]         = useState(false);
    const isDirtyRef = useRef(false);
    isDirtyRef.current = isDirty;
    const [docReloadKey, setDocReloadKey] = useState(0);
    const [loading, setLoading]         = useState(false);
    const [flagSearchText, setFlagSearchText] = useState('');
    const [filterByStatus, setFilterByStatus] = useState(null); // null = all, true = checked, false = unchecked

    const [docNumberConfigs, setDocNumberConfigs] = useState([]);
    const [docNumberDirty, setDocNumberDirty]     = useState(false);
    const [docNumberLoading, setDocNumberLoading] = useState(false);
    const [maxNumbers, setMaxNumbers]             = useState({});
    const [seriesConfigs, setSeriesConfigs]       = useState({});

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [openSections, setOpenSections] = useState({ docNumbering: false, seriesNumbering: false });

    const documentTypeGroups = {
        Sales: [
            { name: 'Quotation',      icon: '📋' },
            // ❌ COMMENTED: Proforma Invoice
            // { name: 'Proforma Invoice', icon: '📄' },
            // ❌ COMMENTED: Sale Order
            // { name: 'Sale Order',     icon: '🛍️' },
            { name: 'Sale Challan',   icon: '📦' },
            { name: 'Sale Invoice',   icon: '🧾' },
        ],
        Purchase: [
            // ❌ COMMENTED: Purchase Order
            // { name: 'Purchase Order',   icon: '📑' },
            // ❌ COMMENTED: Purchase Challan
            // { name: 'Purchase Challan', icon: '📮' },
            // ❌ COMMENTED: Purchase Invoice
            // { name: 'Purchase Invoice', icon: '💳' },
        ],
        Others: [
            { name: 'Credit Note', icon: '✅' },
            // ❌ COMMENTED: Debit Note
            // { name: 'Debit Note',  icon: '❌' },
        ],
    };

    // A different company means the old unsaved edits no longer apply.
    useEffect(() => { isDirtyRef.current = false; setIsDirty(false); }, [companyId]);

    useEffect(() => {
        // Do not overwrite unsaved toggles with a background refresh.
        if (isDirtyRef.current) return;
        if (!configureData?.length) { setForm([]); return; }
        setForm(configureData.map(item => ({
            id:    item.ConfigAutoId,
            label: item.DisplayMessage,
            value: Boolean(item.ApplyConfigure),
        })));
        setIsDirty(false);
    }, [configureData]);

    useEffect(() => {
        if (!companyId) return;
        const load = async () => {
            try {
                const [configRes, maxRes, seriesRes] = await Promise.all([
                    axios.get(`${connectionString}/api/doc-number-config/${companyId}`),
                    axios.get(`${connectionString}/api/doc-number-config/${companyId}/max-numbers`),
                    axios.get(`${connectionString}/api/doc-number-config/${companyId}/series-config`),
                ]);
                const configs     = configRes.data?.data  || [];
                const maxData     = maxRes.data?.data     || {};
                const seriesData  = seriesRes.data?.data  || {};
                setMaxNumbers(maxData);

                const allDocTypes = Object.values(documentTypeGroups).flat().filter(d => !SERIES_DOC_TYPES.has(d.name));
                setDocNumberConfigs(allDocTypes.map(doc => {
                    const existing = configs.find(c => c.BookTypeName === doc.name);
                    return {
                        bookTypeName:    doc.name,
                        startFromNumber: existing ? parseInt(existing.StartFromNumber, 10) : 1,
                    };
                }));

                // seriesData shape: { 'Sale Invoice': [{bookMastId, seriesName, prefix, currentMax, startFromNumber, isLocked}], ... }
                setSeriesConfigs(seriesData);
                setDocNumberDirty(false);
            } catch (err) {
                console.error('Failed to load doc number configs:', err);
            }
        };
        load();
    }, [companyId, connectionString, docReloadKey]);

    const handleFlagChange = (id) => (e) => {
        setForm(prev => prev.map(item => item.id === id ? { ...item, value: e.target.checked } : item));
        setIsDirty(true);
    };

    const handleDocNumberChange = (bookTypeName) => (e) => {
        if ((maxNumbers[bookTypeName] || 0) > 0) return;
        const num = parseInt(e.target.value, 10);
        if (isNaN(num) || num < 1 || num > 99999) return;
        setDocNumberConfigs(prev =>
            prev.map(item => item.bookTypeName === bookTypeName ? { ...item, startFromNumber: num } : item)
        );
        setDocNumberDirty(true);
    };

    const handleSeriesChange = (bookTypeName, bookMastId) => (e) => {
        const raw = e.target.value;
        const num = raw === '' ? null : parseInt(raw, 10);
        if (num !== null && (isNaN(num) || num < 2 || num > 99999)) return;
        setSeriesConfigs(prev => ({
            ...prev,
            [bookTypeName]: (prev[bookTypeName] || []).map(s =>
                s.bookMastId === bookMastId ? { ...s, startFromNumber: num } : s
            ),
        }));
        setDocNumberDirty(true);
    };

    const handleFlagsSubmit = async () => {
        setLoading(true);
        try {
            await axios.put(`${connectionString}/api/configure/update`, {
                userId,
                compId: companyId,
                config: form.map(item => ({ id: item.id, value: item.value })),
            });
            setSnackbar({ open: true, message: 'Feature flags updated successfully', severity: 'success' });
            setIsDirty(false);
            // The save already succeeded; a failed refresh must not be reported as a failed save.
            try {
                await refreshConfigure();
                await refetchConfigureData();
            } catch (refreshErr) {
                console.error('Flag refresh after save failed:', refreshErr);
            }
        } catch {
            setSnackbar({ open: true, message: 'Failed to update feature flags', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDocNumberSubmit = async () => {
        setDocNumberLoading(true);
        const failed = [];

        // Save flat doc type configs
        await Promise.all(docNumberConfigs.map(async (config) => {
            if ((maxNumbers[config.bookTypeName] || 0) > 0) return;
            try {
                await axios.post(`${connectionString}/api/doc-number-config`, {
                    compId: companyId,
                    bookTypeName: config.bookTypeName,
                    startFromNumber: config.startFromNumber,
                    userId,
                    appRegId,
                });
            } catch (err) {
                failed.push(config.bookTypeName);
            }
        }));

        // Save per-series configs — only when startFromNumber >= 2 (never store 1 or null)
        const seriesSaveTasks = [];
        for (const [bookTypeName, seriesList] of Object.entries(seriesConfigs)) {
            for (const s of seriesList) {
                if (s.isLocked) continue;
                if (s.startFromNumber === null || s.startFromNumber < 2) continue;
                seriesSaveTasks.push(
                    axios.post(`${connectionString}/api/doc-number-config`, {
                        compId: companyId,
                        bookTypeName,
                        bookMastId: s.bookMastId,
                        startFromNumber: s.startFromNumber,
                        userId,
                        appRegId,
                    }).catch(() => failed.push(`${bookTypeName} — ${s.seriesName}`))
                );
            }
        }
        await Promise.all(seriesSaveTasks);

        setDocNumberLoading(false);
        if (failed.length > 0) {
            setSnackbar({ open: true, message: `Failed to save: ${failed.join(', ')}`, severity: 'error' });
        } else {
            setSnackbar({ open: true, message: 'Document numbering saved successfully', severity: 'success' });
            setDocNumberDirty(false);
            setDocReloadKey(k => k + 1);
        }
    };

    const fYear = (() => {
        const m = new Date().getMonth() + 1, y = new Date().getFullYear();
        const s = m >= 4 ? y : y - 1;
        return `${String(s).slice(-2)}-${String(s + 1).slice(-2)}`;
    })();

    const flatDocTypes  = Object.values(documentTypeGroups).flat().filter(d => !SERIES_DOC_TYPES.has(d.name));
    // 4 separate series sections: Sale Challan and Sale Invoice are split even though they share bookmaster series
    const seriesDocTypeNames = [
        // 'Proforma Invoice',
        'Sale Challan', 'Sale Invoice', 'Credit Note'];
    const totalLocked   = Object.values(maxNumbers).filter(v => v > 0).length;
    const totalEditable = flatDocTypes.length - totalLocked;

    // Filter feature flags based on search and status
    let filteredForm = form;

    // Apply search filter
    if (flagSearchText.trim() !== '') {
        filteredForm = filteredForm.filter(item =>
            item.label.toLowerCase().includes(flagSearchText.toLowerCase())
        );
    }

    // Apply status filter
    if (filterByStatus !== null) {
        filteredForm = filteredForm.filter(item => item.value === filterByStatus);
    }

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #F3F6FC 0%, #EEF2FA 100%)', display: 'flex', flexDirection: 'column' }}>

            <style>{`@keyframes cfgFadeUp { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
            {/* ── Page Header ── */}
            <Box sx={{
                background: '#0b70f3 ',
                px: { xs: 2, md: 3 }, py: 1.5,
                boxShadow: '0 6px 16px rgba(24,119,242,0.22)',
            }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: C.white, letterSpacing: '0.1px' }}>Configuration Settings</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)', mt: 0.3 }}>
                    Manage system configurations and document numbering preferences
                </Typography>
            </Box>

            {/* ── Scrollable body ── */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, md: 3 }, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* ════ SECTION 1 — Document Numbering ════ */}
                <Box sx={{ bgcolor: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 6px 18px rgba(15,23,42,0.05)', overflow: 'hidden' }}>

                    {/* Section header */}
                    <Box onClick={() => setOpenSections(o => ({ ...o, docNumbering: !o.docNumbering }))} role="button" tabIndex={0} aria-expanded={openSections.docNumbering} sx={{ cursor: 'pointer', transition: 'background .25s ease', '&:hover': { background: C.surface }, px: 2, py: 1.4, background: 'linear-gradient(160deg, #FFFFFF 0%, #F7FAFF 100%)', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 1.1, flexWrap: 'wrap' }}>
                        <Box sx={{ width: 30, height: 30, borderRadius: '9px', bgcolor: C.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                            📊
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: C.text, lineHeight: 1.2 }}>
                                Document Numbering
                            </Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: C.textSub }}>
                                Numbers reset to 1 automatically each financial year
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Chip label={`FY ${fYear}`} size="small" sx={{ height: 20, bgcolor: C.primaryBg, color: C.primary, fontWeight: 700, fontSize: 10, borderRadius: '7px' }} />
                            {totalLocked > 0 && (
                                <Chip label={`${totalLocked} locked`} size="small" sx={{ height: 20, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700, fontSize: 10, borderRadius: '7px' }} />
                            )}
                            {totalEditable > 0 && (
                                <Chip label={`${totalEditable} editable`} size="small" sx={{ height: 20, bgcolor: C.successBg, color: C.success, fontWeight: 700, fontSize: 10, borderRadius: '7px' }} />
                            )}
                        </Box>
                    <Box sx={{ ml: 'auto', color: C.textSub, fontSize: 12, transform: openSections.docNumbering ? 'rotate(180deg)' : 'none', transition: 'transform .3s cubic-bezier(0.16, 1, 0.3, 1)' }}>▾</Box>
                    </Box>

                    <Collapse in={openSections.docNumbering} unmountOnExit timeout={{ enter: 280, exit: 200 }}>
                    {/* Info banner */}
                    <Box sx={{ mx: 2, mt: 1.5, mb: 0, px: 1.5, py: 0.9, bgcolor: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', display: 'flex', alignItems: 'flex-start', gap: 0.85 }}>
                        <Typography sx={{ fontSize: 12, flexShrink: 0 }}>ℹ️</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: '#92400E', lineHeight: 1.5 }}>
                            Once a document is created for the current FY, the starting number is <strong>locked</strong> and cannot be changed. Configure before creating your first document of each type.
                        </Typography>
                    </Box>

                    {/* Groups */}
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {Object.entries(documentTypeGroups).map(([groupName, docs]) => {
                            const meta         = GROUP_META[groupName] || { icon: '📁', color: C.primary };
                            const flatDocs     = docs.filter(d => !SERIES_DOC_TYPES.has(d.name));
                            const groupConfigs = docNumberConfigs.filter(c => flatDocs.some(d => d.name === c.bookTypeName));
                            if (groupConfigs.length === 0) return null;

                            return (
                                <Box key={groupName}>
                                    {/* Group label row */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, mb: 1 }}>
                                        <Box sx={{ width: 3, height: 15, borderRadius: '2px', bgcolor: meta.color }} />
                                        <Typography sx={{ fontWeight: 800, color: C.text, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.64rem' }}>
                                            {meta.icon}&nbsp; {groupName}
                                        </Typography>
                                        <Box sx={{ flex: 1, height: 1, bgcolor: C.border, ml: 0.75 }} />
                                        <Typography sx={{ fontSize: '0.64rem', color: C.textMuted }}>{flatDocs.length} types</Typography>
                                    </Box>

                                    {/* Cards grid */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 1 }}>
                                        {groupConfigs.map(config => {
                                            const docInfo    = flatDocs.find(d => d.name === config.bookTypeName);
                                            const currentMax = maxNumbers[config.bookTypeName] || 0;
                                            const isLocked   = currentMax > 0;

                                            return (
                                                <Tooltip
                                                    key={config.bookTypeName}
                                                    title={isLocked ? `Locked — ${currentMax} document${currentMax !== 1 ? 's' : ''} exist this FY` : ''}
                                                    placement="top"
                                                    arrow
                                                >
                                                    <Box
                                                        sx={{
                                                            p: 1.1,
                                                            borderRadius: '10px',
                                                            border: `1.5px solid ${isLocked ? C.lockedBorder : C.border}`,
                                                            bgcolor: isLocked ? C.locked : C.white,
                                                            boxShadow: isLocked ? 'none' : '0 3px 10px rgba(15,23,42,0.04)',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.6,
                                                            transition: 'transform .25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow .25s ease, border-color .25s ease',
                                                            animation: 'cfgFadeUp .4s cubic-bezier(0.16, 1, 0.3, 1) both',
                                                            ...(!isLocked && {
                                                                '&:hover': {
                                                                    borderColor: C.primary,
                                                                    boxShadow: `0 8px 20px rgba(24,119,242,0.14), 0 0 0 3px ${C.primaryBg}`,
                                                                    transform: 'translateY(-2px)',
                                                                },
                                                                '&:active': { transform: 'translateY(0) scale(0.99)' },
                                                            }),
                                                        }}
                                                    >
                                                        {/* Card top row */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                                                <Typography sx={{ fontSize: 12, lineHeight: 1, flexShrink: 0 }}>{docInfo?.icon}</Typography>
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '0.68rem', fontWeight: 600,
                                                                        color: isLocked ? C.lockedText : C.text,
                                                                        lineHeight: 1.3, wordBreak: 'break-word',
                                                                    }}
                                                                >
                                                                    {config.bookTypeName}
                                                                </Typography>
                                                            </Box>
                                                            {isLocked && (
                                                                <Typography sx={{ fontSize: 11, flexShrink: 0 }}>🔒</Typography>
                                                            )}
                                                        </Box>

                                                        {/* Input */}
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            value={config.startFromNumber > 0 ? config.startFromNumber : ''}
                                                            onChange={handleDocNumberChange(config.bookTypeName)}
                                                            disabled={isLocked}
                                                            placeholder="1"
                                                            inputProps={{ min: 1, max: 99999, step: 1 }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: '7px',
                                                                    bgcolor: isLocked ? '#F9FAFB' : C.white,
                                                                    fontSize: 12,
                                                                    '& fieldset': { borderColor: isLocked ? C.lockedBorder : C.border },
                                                                    '&:hover fieldset': { borderColor: isLocked ? C.lockedBorder : C.primary },
                                                                    '&.Mui-focused fieldset': { borderColor: C.primary },
                                                                    '&.Mui-disabled .MuiOutlinedInput-input': { WebkitTextFillColor: C.lockedText, cursor: 'not-allowed' },
                                                                },
                                                                '& .MuiOutlinedInput-input': { fontWeight: 700, py: '5px', px: 1, fontSize: 12.5 },
                                                            }}
                                                        />

                                                        {/* Status line */}
                                                        <Typography sx={{ fontSize: '0.63rem', lineHeight: 1.3, color: isLocked ? C.warning : C.textMuted, fontWeight: isLocked ? 600 : 400 }}>
                                                            {isLocked
                                                                ? `Max reached: ${currentMax}`
                                                                : 'Set starting number'
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    </Collapse>
                </Box>

                {/* ════ SECTION 1B — Series-based Document Numbering ════ */}
                {seriesDocTypeNames.some(name => (seriesConfigs[name] || []).length > 0) && (
                    <Box sx={{ bgcolor: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 6px 18px rgba(15,23,42,0.05)', overflow: 'hidden' }}>

                        {/* Section header */}
                        <Box onClick={() => setOpenSections(o => ({ ...o, seriesNumbering: !o.seriesNumbering }))} role="button" tabIndex={0} aria-expanded={openSections.seriesNumbering} sx={{ cursor: 'pointer', transition: 'background .25s ease', '&:hover': { background: C.surface }, px: 2, py: 1.4, background: 'linear-gradient(160deg, #FFFFFF 0%, #F7FAFF 100%)', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 1.1, flexWrap: 'wrap' }}>
                            <Box sx={{ width: 30, height: 30, borderRadius: '9px', bgcolor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                                📂
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: C.text, lineHeight: 1.2 }}>
                                    Series-based Document Numbering
                                </Typography>
                                <Typography sx={{ fontSize: '0.68rem', color: C.textSub }}>
                                    Each book series has its own starting number — leave blank to use automatic numbering
                                </Typography>
                            </Box>
                            <Chip label={`FY ${fYear}`} size="small" sx={{ height: 20, bgcolor: C.primaryBg, color: C.primary, fontWeight: 700, fontSize: 10, borderRadius: '7px' }} />
                        <Box sx={{ ml: 'auto', color: C.textSub, fontSize: 12, transform: openSections.seriesNumbering ? 'rotate(180deg)' : 'none', transition: 'transform .3s cubic-bezier(0.16, 1, 0.3, 1)' }}>▾</Box>
                        </Box>

                        <Collapse in={openSections.seriesNumbering} unmountOnExit timeout={{ enter: 280, exit: 200 }}>
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {seriesDocTypeNames.map(docTypeName => {
                                const seriesList = seriesConfigs[docTypeName] || [];
                                if (seriesList.length === 0) return null;
                                const docIcon = Object.values(documentTypeGroups).flat().find(d => d.name === docTypeName)?.icon || '📄';
                                return (
                                    <Box key={docTypeName}>
                                        {/* Doc type header */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, mb: 1 }}>
                                            <Typography sx={{ fontSize: 13 }}>{docIcon}</Typography>
                                            <Typography sx={{ fontWeight: 700, color: C.text, fontSize: '0.72rem' }}>
                                                {docTypeName}
                                            </Typography>
                                            <Box sx={{ flex: 1, height: 1, bgcolor: C.border, ml: 0.75 }} />
                                            <Typography sx={{ fontSize: '0.64rem', color: C.textMuted }}>{seriesList.length} series</Typography>
                                        </Box>

                                        {/* Series cards */}
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 1 }}>
                                            {seriesList.map(s => (
                                                <Tooltip
                                                    key={s.bookMastId}
                                                    title={s.isLocked ? `Locked — max series no: ${s.currentMax}` : ''}
                                                    placement="top"
                                                    arrow
                                                >
                                                    <Box
                                                        sx={{
                                                            p: 1.1,
                                                            borderRadius: '10px',
                                                            border: `1.5px solid ${s.isLocked ? C.lockedBorder : C.border}`,
                                                            bgcolor: s.isLocked ? C.locked : C.white,
                                                            boxShadow: s.isLocked ? 'none' : '0 3px 10px rgba(15,23,42,0.04)',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.6,
                                                            transition: 'border-color 0.15s, box-shadow 0.15s',
                                                            ...(!s.isLocked && {
                                                                '&:hover': {
                                                                    borderColor: C.primary,
                                                                    boxShadow: `0 0 0 3px ${C.primaryBg}`,
                                                                }
                                                            }),
                                                        }}
                                                    >
                                                        {/* Series name + lock */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: s.isLocked ? C.lockedText : C.text, lineHeight: 1.3, wordBreak: 'break-word' }}>
                                                                    {s.seriesName}
                                                                </Typography>
                                                                {s.prefix && (
                                                                    <Chip label={s.prefix} size="small" sx={{ mt: 0.4, height: 16, fontSize: 9, fontWeight: 700, bgcolor: C.primaryBg, color: C.primary, borderRadius: '5px', '& .MuiChip-label': { px: 0.6 } }} />
                                                                )}
                                                            </Box>
                                                            {s.isLocked && <Typography sx={{ fontSize: 11, flexShrink: 0 }}>🔒</Typography>}
                                                        </Box>

                                                        {/* Input */}
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            type="number"
                                                            value={s.startFromNumber !== null ? s.startFromNumber : ''}
                                                            onChange={handleSeriesChange(docTypeName, s.bookMastId)}
                                                            disabled={s.isLocked}
                                                            placeholder="Auto"
                                                            inputProps={{ min: 2, max: 99999, step: 1 }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: '7px',
                                                                    bgcolor: s.isLocked ? '#F9FAFB' : C.white,
                                                                    fontSize: 12,
                                                                    '& fieldset': { borderColor: s.isLocked ? C.lockedBorder : C.border },
                                                                    '&:hover fieldset': { borderColor: s.isLocked ? C.lockedBorder : C.primary },
                                                                    '&.Mui-focused fieldset': { borderColor: C.primary },
                                                                    '&.Mui-disabled .MuiOutlinedInput-input': { WebkitTextFillColor: C.lockedText, cursor: 'not-allowed' },
                                                                },
                                                                '& .MuiOutlinedInput-input': { fontWeight: 700, py: '5px', px: 1, fontSize: 12.5 },
                                                            }}
                                                        />

                                                        {/* Status */}
                                                        <Typography sx={{ fontSize: '0.63rem', lineHeight: 1.3, color: s.isLocked ? C.warning : C.textMuted, fontWeight: s.isLocked ? 600 : 400 }}>
                                                            {s.isLocked
                                                                ? `Current max: ${s.currentMax}`
                                                                : s.startFromNumber !== null
                                                                    ? `Starts from ${s.startFromNumber}`
                                                                    : 'Auto (continues from max)'
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Collapse>
                    </Box>
                )}

                {/* ════ SECTION 2 — Feature Flags ════ */}
                <Box sx={{ bgcolor: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, boxShadow: '0 6px 18px rgba(15,23,42,0.05)', overflow: 'hidden' }}>

                    {/* Section header */}
                    <Box sx={{ px: 2, py: 1.4, background: 'linear-gradient(160deg, #FFFFFF 0%, #F7FAFF 100%)', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 1.1, flexWrap: 'wrap' }}>
                        <Box sx={{ width: 30, height: 30, borderRadius: '9px', bgcolor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>
                            ⚙️
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: C.text, lineHeight: 1.2 }}>
                                Feature Flags
                            </Typography>
                            <Typography sx={{ fontSize: '0.68rem', color: C.textSub }}>
                                Enable or disable system features for this company
                            </Typography>
                        </Box>
                        <Chip
                            label={`${form.filter(f => f.value).length} / ${form.length} enabled`}
                            size="small"
                            sx={{ height: 20, bgcolor: form.filter(f => f.value).length > 0 ? C.successBg : C.surface, color: form.filter(f => f.value).length > 0 ? C.success : C.textSub, fontWeight: 700, fontSize: 10, borderRadius: '7px' }}
                        />
                    </Box>

                    {/* Search field and filter toggle */}
                    <Box sx={{ px: 2, py: 1.4, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TextField
                            placeholder="Search feature flags..."
                            size="small"
                            value={flagSearchText}
                            onChange={(e) => setFlagSearchText(e.target.value)}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    fontSize: 12.5,
                                    borderRadius: '8px',
                                    bgcolor: C.surface,
                                    '& fieldset': { borderColor: C.border },
                                    '&:hover fieldset': { borderColor: C.primary },
                                    '&.Mui-focused fieldset': { borderColor: C.primary, borderWidth: 1.5 },
                                },
                                '& .MuiOutlinedInput-input': { py: '6px' },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: C.textMuted, fontSize: 13 }}>
                                        🔍
                                    </Box>
                                ),
                            }}
                        />
                        {/* Sliding dot toggle with labels below */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Box
                                sx={{
                                    width: '96px',
                                    height: '26px',
                                    bgcolor: '#E5E7EB',
                                    borderRadius: '13px',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    padding: '3px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                                onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const clickX = e.clientX - rect.left;
                                    const toggleWidth = rect.width - 6;
                                    const position = clickX / toggleWidth;

                                    if (position < 0.33) {
                                        setFilterByStatus(true);
                                    } else if (position < 0.67) {
                                        setFilterByStatus(null);
                                    } else {
                                        setFilterByStatus(false);
                                    }
                                }}
                            >
                                {/* Sliding dot */}
                                <Box
                                    sx={{
                                        width: '20px',
                                        height: '20px',
                                        bgcolor: C.primary,
                                        borderRadius: '50%',
                                        position: 'absolute',
                                        transition: 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        left: filterByStatus === true ? '3px' : filterByStatus === null ? '38px' : '73px',
                                        boxShadow: '0 1px 4px rgba(24, 119, 242, 0.3)',
                                        top: '3px'
                                    }}
                                />
                            </Box>
                            {/* Labels below toggle */}
                            <Box sx={{ display: 'flex', width: '96px', justifyContent: 'space-between', px: 0.4 }}>
                                <Typography sx={{ color: C.textMuted, fontWeight: 500, fontSize: 8.5 }}>Check</Typography>
                                <Typography sx={{ color: C.textMuted, fontWeight: 500, fontSize: 8.5 }}>All</Typography>
                                <Typography sx={{ color: C.textMuted, fontWeight: 500, fontSize: 8.5 }}>Uncheck</Typography>
                            </Box>
                        </Box>
                        {flagSearchText && (
                            <Chip
                                label={`${filteredForm.length} / ${form.length} shown`}
                                size="small"
                                onDelete={() => setFlagSearchText('')}
                                sx={{ height: 20, bgcolor: C.primaryBg, color: C.primary, fontWeight: 700, fontSize: 10, borderRadius: '7px' }}
                            />
                        )}
                    </Box>

                    {/* Flags cards grid */}
                    <Box sx={{ p: 2 }}>
                        {form.length === 0 ? (
                            <Box sx={{ py: 3, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: C.textMuted }}>No feature flags configured</Typography>
                            </Box>
                        ) : filteredForm.length === 0 ? (
                            <Box sx={{ py: 3, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: '0.75rem', color: C.textMuted }}>No feature flags match your search</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: 1 }}>
                                {filteredForm.map(item => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            p: 1.1,
                                            borderRadius: '10px',
                                            border: `1.5px solid ${item.value ? C.primary : C.border}`,
                                            bgcolor: item.value ? C.primaryBg : C.white,
                                            boxShadow: item.value ? '0 3px 10px rgba(24,119,242,0.1)' : '0 3px 10px rgba(15,23,42,0.04)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.6,
                                            transition: 'transform .25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow .25s ease, border-color .25s ease',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            animation: 'cfgFadeUp .4s cubic-bezier(0.16, 1, 0.3, 1) both',
                                            '&:hover': {
                                                borderColor: C.primary,
                                                boxShadow: `0 8px 20px rgba(24,119,242,0.14), 0 0 0 3px ${C.primaryBg}`,
                                                transform: 'translateY(-2px)',
                                            },
                                            '&:active': { transform: 'translateY(0) scale(0.99)' },
                                        }}
                                        onClick={() => handleFlagChange(item.id)({ target: { checked: !item.value } })}
                                    >
                                        {/* Card top row - checkbox and status icon */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                                <Checkbox
                                                    checked={item.value}
                                                    onChange={handleFlagChange(item.id)}
                                                    onClick={e => e.stopPropagation()}
                                                    size="small"
                                                    sx={{
                                                        p: 0, flexShrink: 0,
                                                        color: C.textMuted,
                                                        '& .MuiSvgIcon-root': { fontSize: 17 },
                                                        '&.Mui-checked': { color: C.primary },
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.68rem', fontWeight: 600,
                                                        color: item.value ? C.primary : C.text,
                                                        lineHeight: 1.3, wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {item.label}
                                                </Typography>
                                            </Box>
                                            {item.value && (
                                                <Typography sx={{ fontSize: 11, flexShrink: 0 }}>✓</Typography>
                                            )}
                                        </Box>

                                        {/* Status line */}
                                        <Typography sx={{ fontSize: '0.63rem', lineHeight: 1.3, color: item.value ? C.primary : C.textMuted, fontWeight: item.value ? 600 : 400 }}>
                                            {item.value ? 'Enabled' : 'Disabled'}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>

            </Box>

            {/* ── Sticky Footer ── */}
            <Box sx={{
                position: 'sticky', bottom: 0, zIndex: 10,
                bgcolor: C.white, borderTop: `1px solid ${C.border}`,
                boxShadow: '0 -3px 12px rgba(15,23,42,0.04)',
                px: { xs: 1.5, md: 3 }, py: 1.1,
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1,
            }}>
                {isDirty && (
                    <Typography sx={{ fontSize: '0.68rem', color: C.textMuted, mr: 'auto', fontWeight: 500 }}>
                        You have unsaved changes
                    </Typography>
                )}
                <Button
                    variant="outlined"
                    onClick={handleFlagsSubmit}
                    disabled={!isDirty || loading}
                    sx={{
                        height: 32, px: 2, borderRadius: '8px', fontWeight: 700, fontSize: 12, textTransform: 'none',
                        borderColor: C.border, color: C.text,
                        '&:not(:disabled):hover': { borderColor: C.primary, color: C.primary, bgcolor: C.primaryBg },
                        '&:disabled': { color: C.textMuted, borderColor: C.border },
                    }}
                >
                    {loading ? 'Saving…' : 'Save Feature Flags'}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleDocNumberSubmit}
                    disabled={!docNumberDirty || docNumberLoading}
                    disableElevation
                    sx={{
                        height: 32, px: 2, borderRadius: '8px', fontWeight: 700, fontSize: 12, textTransform: 'none',
                        background: 'linear-gradient(135deg, #1877F2 0%, #0855A8 100%)',
                        boxShadow: '0 6px 16px rgba(24,119,242,0.3)',
                        '&:hover': { boxShadow: '0 8px 18px rgba(24,119,242,0.4)' },
                        '&:disabled': { background: C.border, color: C.textMuted, boxShadow: 'none' },
                    }}
                >
                    {docNumberLoading ? 'Saving…' : 'Save Document Numbers'}
                </Button>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '12px', fontWeight: 700, boxShadow: '0 10px 28px rgba(15,23,42,0.2)' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ConfigureModule;
